import { useState } from 'react';
import { supabase } from '@/lib/supabase';
import { mensagemAmigavel } from '@/lib/mensagemAmigavel';
import Modal from '@/components/ui/Modal';
import Botao from '@/components/ui/Botao';
import { formatarCentavos } from '@/pages/loja/utils';
import { horaBrasilia, normalizarTurno, parseDinheiroCentavos } from '../turno';
import type { CaixaTurno } from '../types';

interface Props {
  turno: CaixaTurno;
  aoFechar: () => void;
  aoRegistrar: (turno: CaixaTurno) => void;
}

interface Recibo {
  valor_centavos: number;
  em: string;
  operador: string | null;
}

// Sangria: dinheiro que sai da gaveta durante o turno (entregue a alguém da direção).
export default function ModalSangria({ turno, aoFechar, aoRegistrar }: Props) {
  const [texto, setTexto] = useState('');
  const [obs, setObs] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [recibo, setRecibo] = useState<Recibo | null>(null);

  async function registrar() {
    const centavos = parseDinheiroCentavos(texto);
    if (centavos == null || centavos <= 0) {
      setErro('Digite quanto vai tirar da gaveta.');
      return;
    }
    setEnviando(true);
    setErro(null);
    try {
      const { data, error } = await supabase.rpc('caixa_sangria', {
        p_turno: turno.turno_id,
        p_valor_centavos: centavos,
        p_observacao: obs.trim() || null,
      });
      if (error) throw error;
      const novo = normalizarTurno(data) ?? turno;
      aoRegistrar(novo);
      const ultima = novo.sangrias.length ? novo.sangrias[novo.sangrias.length - 1] : null;
      setRecibo({
        valor_centavos: centavos,
        em: ultima?.em || new Date().toISOString(),
        operador: novo.operador ?? turno.operador ?? null,
      });
    } catch (e) {
      setErro(mensagemAmigavel(e));
    } finally {
      setEnviando(false);
    }
  }

  if (recibo) {
    return (
      <Modal aberto titulo="Sangria registrada" aoFechar={aoFechar}>
        <div className="rounded-2xl border border-success-200 bg-success-50 px-4 py-4">
          <p className="flex items-center gap-2 text-[15px] font-semibold text-success-800">
            <span className="flex h-5 w-5 items-center justify-center">
              <i className="ri-checkbox-circle-line" />
            </span>
            Guarde este comprovante com o dinheiro
          </p>
          <div className="mt-3 space-y-1 text-[15px] text-foreground-900">
            <p>
              Retirado da gaveta:{' '}
              <span className="font-bold text-foreground-950">{formatarCentavos(recibo.valor_centavos)}</span>
            </p>
            <p>Hora: {horaBrasilia(recibo.em)}</p>
            <p>Operador: {recibo.operador || '—'}</p>
            {obs.trim() && <p>Entregue a: {obs.trim()}</p>}
          </div>
        </div>

        <div className="mt-5">
          <Botao tamanho="lg" cheio onClick={aoFechar}>
            Fechar
          </Botao>
        </div>
      </Modal>
    );
  }

  return (
    <Modal aberto titulo="Sangria" aoFechar={aoFechar}>
      <p className="text-[15px] text-foreground-700">Quanto vai tirar da gaveta?</p>
      <p className="mt-1 text-[13px] text-foreground-500">
        Na gaveta agora: <span className="font-semibold">{formatarCentavos(turno.esperado_centavos)}</span>
      </p>

      <input
        type="text"
        inputMode="decimal"
        value={texto}
        onChange={(e) => {
          setTexto(e.target.value.replace(/[^0-9.,]/g, ''));
          setErro(null);
        }}
        placeholder="0,00"
        autoFocus
        className="mt-3 w-full rounded-xl border-2 border-secondary-300 bg-white px-4 py-2.5 text-lg font-bold tabular-nums text-foreground-950 outline-none focus:border-primary-500"
      />

      <div className="mt-4">
        <label htmlFor="sangria-obs" className="block text-sm font-medium text-foreground-900">
          Observação (opcional)
        </label>
        <input
          id="sangria-obs"
          type="text"
          value={obs}
          onChange={(e) => setObs(e.target.value)}
          placeholder="Entregue ao Fulano"
          className="mt-1 w-full rounded-xl border-2 border-secondary-300 bg-white px-4 py-2.5 text-sm text-foreground-950 outline-none focus:border-primary-500"
        />
      </div>

      {erro && (
        <p className="mt-3 flex items-start gap-1.5 text-[14px] text-danger-600" role="alert">
          <span className="mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center">
            <i className="ri-error-warning-line" />
          </span>
          {erro}
        </p>
      )}

      <div className="mt-5">
        <Botao tamanho="lg" cheio carregando={enviando} onClick={() => void registrar()}>
          {enviando ? 'Registrando…' : 'Registrar sangria'}
        </Botao>
      </div>
      <p className="mt-3 text-[13px] leading-relaxed text-foreground-500">
        O valor sai do dinheiro esperado na gaveta e fica registrado no fechamento.
      </p>
    </Modal>
  );
}